/**
 * Where the roll call keeps each person's held chore order (spec D9, Ruling
 * 20) between mounts. The wall unmounts the hub whenever a PIN modal opens
 * (KioskLoginPage), so an order kept in a component ref reset on every locked
 * chore tap and Cancel, and the rows jumped.
 *
 * A module-level map, keyed by family and family day, holds the last result
 * of `holdChoreOrder` (which stays the single, pure rule). It lives for the
 * session: a page reload starts again from the server's order. Only this
 * day's entry is kept for a family.
 */
import type { ChoreOrder } from './rollCallModel';

/**
 * The key the locked wall holds its order under (Ruling 21). The wall is the
 * signed-out device-cookie surface, so it has no family id in the client; one
 * paired device shows one family. Pairing, de-pairing and sign-out clear it.
 */
export const KIOSK_DEVICE_ORDER_KEY = 'kiosk-device';

const held = new Map<string, ChoreOrder>();

const keyOf = (familyId: string, day: number) => `${familyId}|${day}`;

/** The order held for this family on this family day, or null. */
export function readHeldChoreOrder(familyId: string, day: number): ChoreOrder | null {
  return held.get(keyOf(familyId, day)) ?? null;
}

/** Keep `order` for its family day, dropping the family's other days. */
export function writeHeldChoreOrder(familyId: string, order: ChoreOrder): void {
  const prefix = `${familyId}|`;
  for (const key of held.keys()) if (key.startsWith(prefix)) held.delete(key);
  held.set(keyOf(familyId, order.day), order);
}

/** Forget this device's held order: the device was paired, re-paired or de-paired. */
export function forgetDeviceChoreOrder(): void {
  const prefix = `${KIOSK_DEVICE_ORDER_KEY}|`;
  for (const key of held.keys()) if (key.startsWith(prefix)) held.delete(key);
}

/** Forget every held order (sign-out, and tests). */
export function resetHeldChoreOrders(): void {
  held.clear();
}
